import { useRef } from 'react';
import { faArrowDown, faMessages } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAutoFocus } from '@client/new/helpers/useAutoFocus';
import { CommunityId } from '../model/community.types';
import { PostId } from '../model/post.types';
import { useCommunityPostReplies } from '../api/useCommunityPostReplies';
import { useAddReply } from '../api/useAddReply';
import { useReplyScrollBehavior } from '../lib/useReplyScrollBehavior';
import { ReplyCell } from './ReplyCell';
import { ReplySkeleton } from './ReplySkeleton';
import { AddReply } from './AddReply';

const COMMENTS_MAX_HEIGHT_PX = 300;
const MAX_SKELETON_ROWS = 4;

type Props = {
  communityId: CommunityId;
  postId: PostId;
  influencerId?: string | number;
  canModerate: boolean;
  isDmFeatureEnabled: boolean;
  commentCount: number;
};

export const PostReplies = ({
  communityId,
  postId,
  influencerId,
  canModerate,
  isDmFeatureEnabled,
  commentCount,
}: Props) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const { replies, hasMore, isLoadingMore, loadMore } = useCommunityPostReplies(
    communityId,
    postId,
    canModerate,
  );
  const addReply = useAddReply(communityId, postId, canModerate, influencerId);

  const list = replies.type === 'fetched' ? replies.value : [];
  const isInitialLoading =
    replies.type === 'not-fetched' || replies.type === 'fetching';

  const {
    hasNewComment,
    markOwnSubmit,
    captureScrollBeforePagination,
    scrollToNewComment,
    handleScroll,
  } = useReplyScrollBehavior({
    scrollRef,
    isInitialLoading,
    lastReplyId: list[list.length - 1]?.id,
    repliesCount: list.length,
  });

  useAutoFocus({ ref: inputRef, preventScroll: true });

  const handleSubmit = (text: string) => {
    markOwnSubmit();
    addReply(text);
  };

  const handleViewPrevious = () => {
    captureScrollBeforePagination();
    loadMore();
  };

  const skeletonRows = Math.min(commentCount, MAX_SKELETON_ROWS);

  return (
    <div className="flex flex-col">
      {isInitialLoading && skeletonRows > 0 && (
        <div
          className="overflow-hidden"
          style={{ maxHeight: `${COMMENTS_MAX_HEIGHT_PX}px` }}
        >
          {Array.from({ length: skeletonRows }, (_, index) => (
            <ReplySkeleton key={index} />
          ))}
        </div>
      )}
      {list.length > 0 && (
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="overflow-y-auto"
          style={{ maxHeight: `${COMMENTS_MAX_HEIGHT_PX}px` }}
        >
          {hasMore && (
            <div className="mb-3 flex justify-center">
              <button
                type="button"
                onClick={handleViewPrevious}
                disabled={isLoadingMore}
                className="flex items-center gap-2 text-sm text-[#8F94A1]"
              >
                <FontAwesomeIcon icon={faMessages} />
                {isLoadingMore
                  ? 'Loading previous comments'
                  : 'View previous comments'}
              </button>
            </div>
          )}
          {list.map((reply) => (
            <ReplyCell
              key={reply.id}
              reply={reply}
              communityId={communityId}
              postId={postId}
              influencerId={influencerId}
              canModerate={canModerate}
              isDmFeatureEnabled={isDmFeatureEnabled}
            />
          ))}
        </div>
      )}
      <div className="relative">
        {hasNewComment && (
          <button
            type="button"
            onClick={scrollToNewComment}
            className="absolute -top-10 left-0 right-0 mx-auto flex h-7 w-[175px] items-center justify-center gap-2 rounded-full bg-[#5F1ECC] text-white"
          >
            <FontAwesomeIcon icon={faArrowDown} />
            <span>New Comment</span>
          </button>
        )}
        <AddReply inputRef={inputRef} onSubmit={handleSubmit} />
      </div>
    </div>
  );
};
